// src/portal-app/insights/ProductIntelligenceScreen.js
//
// W2 Task 7 — Product Intelligence, the second native Insights surface and
// the first to PUSH a param-carrying sub-screen (ProductDetailScreen — the
// pattern Wave 4's Employee Detail copies). Phone-first rebuild of
// src/pages/portal/reporting/ProductIntelligencePage.js over the SAME data
// services, assembled from the Task-5 kit.
//
// Data — reused UNCHANGED from services/api.js:
//   reportingService.getProductsV2(start, end)        the main payload
//   reportingService.getProductNarrative(start, end)  enhancement — fails soft
//
// Zones: hero product revenue + DeltaPill → AI narrative → KPI grid →
// top contributors → falling behind → category movement (tap-to-disclose
// subcategories) → momentum shifts (velocity watchlist) → top-N full menu.
//
// ⚠️ /products returns NO insights key — deliberately no InsightList here.
// ⚠️ velocity delta_7d_vs_30d is a RATIO — displayed ×100 (the web page's
//    ProductIntelligencePage.js:1380 formula).
// ⚠️ products_full returns EVERY product with a 30-element sparkline — this
//    screen keeps at most MENU_TOP_N rows of it in memory-to-DOM and states
//    the truncation; the sparklines are never rendered.
//
// Deliberately dropped from the web page (desktop-only): the full-products
// modal (search/sort/CSV export), the category expand-to-chart modal, the
// KPI flip cards + concentration radial gauge, per-row sparklines and the
// print/PDF button. Product drill-down is a pushed native screen instead of
// a modal.
//
// W4 Task 15 (P6 — spec §5, conservative pass; NO structural rewrite, zone
// order and every chart unchanged):
//   • hero already carried the P4 GlassCard-veil treatment (HeroValue) —
//     confirmed, not re-authored;
//   • CondensedRow: THE full menu list (products_full — the literal "list of
//     every product", as distinct from the three ranked/comparison charts
//     below) becomes CondensedRow rows — name, revenue value, units
//     secondary, and a margin-band pill (green/amber/red/muted, the same
//     thresholds marginBandToken already used for the BarList fill colour).
//     Nothing dropped: units and margin_pct both still render, just reshaped
//     from one caption string into secondary + pill. Rows push to
//     'product-detail' exactly as before (Task 17). Top contributors,
//     falling behind and category movement stay BarList — each carries a
//     pct-of/vs-comparison a plain row can't express (share of total,
//     decline magnitude, tap-to-disclose subcategories — the last needs
//     BarList's itemProps aria-expanded/aria-controls hook, which
//     CondensedRow has no equivalent for), matching the "kept chart"
//     precedent Tasks 13/14 already set for BarList;
//   • ShareBar: declined. The one genuine part-to-whole quantity on this
//     screen — Revenue Concentration's top-N-vs-rest split — is not
//     currently rendered as a strip of numbers anywhere (only its single %
//     KPI tile + a caption); the web's equivalent was a radial gauge, not a
//     dual-segment bar, and building a new relationship view for a field
//     nothing here currently surfaces would be new content, not a
//     replacement — out of scope for a no-rewrite pass;
//   • collapsed-empty: already satisfied — every zone (KPI grid, each
//     BarList, the subcategory panel, momentum groups, the full menu) is
//     already gated on its own items.length, so nothing here can render a
//     run of dashes;
//   • PullToRefresh now wraps SurfaceStates (RangeBar stays outside both,
//     unchanged, so it keeps surviving loading/error/empty).
//
// This screen renders CONTENT ONLY — NavStackHost owns the header/back
// button (route meta in nav/routes.js).
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GlassCard } from '../shell/LiquidGlass';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { reportingService } from '../../services/api';
import { useNav } from '../nav/NavStack';
import { rangeForPreset, toLocalISO } from '../nav/dateRange';
import { useRangePreset } from './rangeState';
import {
    normaliseKpi,
    fmtMoney,
    HeroValue,
    RangeBar,
    KpiGrid,
    BarList,
    NarrativeCard,
    SurfaceStates,
} from './kit';
import {
    CondensedRow, IconTag, PullToRefresh, ensureKitCss,
} from '../shell/kit';

/* The DOM ceiling for sections.products_full — the payload carries EVERY
   product; the web truncates to 10 in-page. We keep a top-20 slice (10
   collapsed via BarList, +10 behind its disclosure) and state the cut. */
const MENU_TOP_N = 20;
const MENU_COLLAPSED_ROWS = 10;

/* Screen-local chrome only — everything data-bearing comes from the kit
   (which also injects the shared .insk-* stylesheet, incl. .insk-num and
   the delta-pill classes reused by the hero below). */
const CSS = `
.pis-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;}
.pis-vel{padding:16px 18px;margin-bottom:14px;}
.pis-vel-group{margin-bottom:6px;}
.pis-vel-title{display:flex;align-items:center;gap:6px;font-size:11px;font-weight:700;
  letter-spacing:.9px;text-transform:uppercase;color:var(--c-textLight);margin:8px 0 2px;}
.pis-vel-row{display:flex;width:100%;min-height:44px;align-items:center;gap:10px;
  background:none;border:0;margin:0;padding:6px 0;font:inherit;color:inherit;
  text-align:start;cursor:pointer;border-radius:10px;touch-action:manipulation;}
.pis-vel-row:active{background:color-mix(in srgb, var(--c-ink) 6%, transparent);}
.pis-vel-main{flex:1;min-width:0;}
.pis-vel-name{font-size:13px;font-weight:600;color:var(--c-ink);overflow:hidden;
  text-overflow:ellipsis;white-space:nowrap;}
.pis-vel-cap{margin-top:2px;font-size:11px;font-weight:500;color:var(--c-textLight);}
.pis-vel-delta{flex:none;font-size:12.5px;font-weight:700;}
.pis-menu-note{margin:-6px 6px 14px;font-size:11px;font-weight:500;color:var(--c-textLight);}
`;

/* Margin band — the web page's marginBandColor thresholds mapped to tokens:
   green ≥40, amber 20-40, red <20 (null → muted). */
function marginBandToken(pct) {
    if (pct == null) return 'var(--c-mutedLight)';
    if (pct >= 40) return 'var(--c-green)';
    if (pct >= 20) return 'var(--c-amber)';
    return 'var(--c-red)';
}

/* Same margin band, as a shell/kit tone NAME rather than a CSS var — the
   full menu's CondensedRow rows use this to colour both the icon and the
   pill (kit's tone vocabulary, not a raw var). Identical thresholds to
   marginBandToken above so the two lists never disagree on what "healthy
   margin" means. */
function marginBandTone(pct) {
    if (pct == null) return 'muted';
    if (pct >= 40) return 'green';
    if (pct >= 20) return 'amber';
    return 'red';
}

/* Backend KPI names are English literals — map them to t() keys (the web
   page's kpiLabelKey mapping; the keys shipped with it). */
const KPI_LABELS = {
    'Gross Margin': ['kpi_gross_margin', 'Gross Margin'],
    'Units Sold': ['kpi_units_sold', 'Units Sold'],
    'Revenue Concentration': ['kpi_revenue_concentration', 'Revenue Concentration'],
};

/* "Empty" = the period produced nothing to report: zero product revenue and
   no per-product rows at all. */
function isEmptyPayload(d) {
    const kpis = (d && d.kpis) || [];
    const rev = kpis.find((k) => k.name === 'Product Revenue');
    const sections = (d && d.sections) || {};
    const hasProducts = (sections.products_full || []).length > 0
        || (sections.top_contributors || []).length > 0;
    return !((rev && Number(rev.value)) || hasProducts);
}

/* Small directional arrow for the velocity group headers (inline SVG, no
   emoji). */
function VelArrow({ up }) {
    return (
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path
                d={up ? 'M6 2.5 L10 8.5 L2 8.5 Z' : 'M6 9.5 L2 3.5 L10 3.5 Z'}
                fill="currentColor"
            />
        </svg>
    );
}

export default function ProductIntelligenceScreen() {
    ensureKitCss();
    const auth = useAuth() || {};
    const currency = auth.getCurrency ? auth.getCurrency() : '£';
    const { t } = useLanguage();
    const { push } = useNav();

    // Shared across all Insights surfaces (M1) — survives pops and tab
    // switches; the setter coerces junk ids so rangeForPreset never throws.
    const [preset, setPreset] = useRangePreset();
    const range = useMemo(() => rangeForPreset(preset), [preset]);
    const [status, setStatus] = useState('loading'); // loading|error|empty|ready
    const [data, setData] = useState(null);
    const [narrative, setNarrative] = useState(null);
    const [narrativeStatus, setNarrativeStatus] = useState('loading');
    const [openCategory, setOpenCategory] = useState(null);
    // The full menu's own ≤MENU_COLLAPSED_ROWS collapse (BarList used to own
    // this internally; CondensedRow has no built-in pagination).
    const [menuExpanded, setMenuExpanded] = useState(false);

    const startIso = toLocalISO(range.start);
    const endIso = toLocalISO(range.end);

    /* Main fetch — stale responses are dropped by sequence, so a quick
       range flick can never paint an older period over a newer one. */
    const seqRef = useRef(0);
    const load = useCallback(() => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');
        setOpenCategory(null);
        setMenuExpanded(false);
        // Returned (W4 Task 15) so PullToRefresh can await it — its spinner
        // stays up until the fetch actually settles, success or failure.
        return reportingService.getProductsV2(startIso, endIso)
            .then((d) => {
                if (seq !== seqRef.current) return;
                setData(d);
                setStatus(isEmptyPayload(d) ? 'empty' : 'ready');
            })
            .catch(() => {
                if (seq === seqRef.current) setStatus('error');
            });
    }, [startIso, endIso]);
    useEffect(() => { load(); }, [load]);

    /* Narrative — an enhancement with its OWN status; its failure renders
       nothing and never touches the main surface state. */
    useEffect(() => {
        let stale = false;
        setNarrativeStatus('loading');
        reportingService.getProductNarrative(startIso, endIso)
            .then((res) => {
                if (stale) return;
                setNarrative(res);
                setNarrativeStatus('ready');
            })
            .catch(() => {
                if (!stale) setNarrativeStatus('error');
            });
        return () => { stale = true; };
    }, [startIso, endIso]);

    /* THE push-with-params pattern (Wave 4 copies this): route id +
       serialisable params; the detail screen fetches with the SAME range. */
    const openDetail = useCallback((code, name) => {
        push('product-detail', {
            code,
            name,
            range: { start: startIso, end: endIso },
        });
    }, [push, startIso, endIso]);

    const kpis = (data && data.kpis) || [];
    const sections = (data && data.sections) || {};

    /* ---- hero: Product Revenue + delta (normaliseKpi owns the quirks;
       rendering is the kit's HeroValue) ---- */
    const revKpi = kpis.find((k) => k.name === 'Product Revenue') || null;
    const revNorm = revKpi ? normaliseKpi(revKpi) : null;

    /* ---- KPI grid: Gross Margin / Units Sold / Revenue Concentration.
       Concentration carries meta {top_n, top_n_revenue, total_revenue,
       product_count} → a caption instead of the web's radial gauge; its
       invert:true is absorbed by normaliseKpi (a falling share renders
       good). Odd count → last tile spans full width. ---- */
    const restKpis = kpis.filter((k) => k !== revKpi && KPI_LABELS[k.name]);
    const gridItems = restKpis.map((k, i) => {
        const meta = KPI_LABELS[k.name];
        const item = {
            kpi: k,
            label: t(meta[0], {}, meta[1]),
            format: 'number',
            caption: true,
            ...(restKpis.length % 2 === 1 && i === restKpis.length - 1 ? { span: 2 } : {}),
        };
        if (k.name === 'Revenue Concentration' && k.meta) {
            item.caption = t(
                'conc_top_n_caption',
                { n: k.meta.top_n, count: k.meta.product_count },
                'Top {n} of {count} products',
            );
        }
        return item;
    });

    /* ---- top contributors: share of total + margin band colour ---- */
    const contributorItems = (sections.top_contributors || []).map((p) => ({
        label: p.product_name,
        value: Number(p.revenue) || 0,
        pct: p.pct_of_total,
        caption: `${Number(p.units) || 0} ${t('units', {}, 'units')} · ${Number(p.margin_pct || 0).toFixed(1)}% ${t('margin', {}, 'margin')}`,
        color: marginBandToken(p.margin_pct),
        code: p.product_code,
    }));

    /* ---- falling behind: worst decliners (backend sort preserved) ---- */
    const fallingItems = (sections.falling_behind || []).map((p) => ({
        label: p.product_name,
        value: Number(p.current_revenue) || 0,
        caption: t(
            'decline_caption',
            {
                prev: fmtMoney(p.previous_revenue, currency),
                pct: Math.abs(Number(p.revenue_delta_pct) || 0).toFixed(1),
            },
            'was {prev} · down {pct}%',
        ),
        color: 'var(--c-red)',
        code: p.product_code,
    }));

    /* ---- category movement: tap a category to disclose subcategories ---- */
    const categories = sections.category_movement || [];
    const categoryItems = categories.map((c) => ({
        label: c.category,
        value: Number(c.revenue) || 0,
        pct: c.pct_of_total,
        caption: c.revenue_delta_pct != null
            ? `${c.revenue_delta_pct > 0 ? '+' : ''}${Number(c.revenue_delta_pct).toFixed(1)}% · ${c.product_count} ${t('products', {}, 'products')}`
            : `${c.product_count} ${t('products', {}, 'products')}`,
    }));
    const openCat = categories.find((c) => c.category === openCategory);
    const subItems = ((openCat && openCat.subcategories) || []).map((s) => ({
        label: s.subcategory,
        value: Number(s.revenue) || 0,
        pct: s.pct_of_total,
        caption: s.revenue_delta_pct != null
            ? `${s.revenue_delta_pct > 0 ? '+' : ''}${Number(s.revenue_delta_pct).toFixed(1)}%`
            : undefined,
    }));

    /* ---- momentum shifts: delta_7d_vs_30d is a RATIO → ×100 (the web's
       line-1380 formula), 0 digits like the web badge ---- */
    const watchlist = sections.velocity_watchlist || {};
    const velocityGroups = [
        {
            id: 'accelerating',
            title: t('gaining_momentum', {}, 'Gaining momentum'),
            up: true,
            color: 'var(--c-green)',
            rows: watchlist.accelerating || [],
        },
        {
            id: 'decelerating',
            title: t('losing_momentum', {}, 'Losing momentum'),
            up: false,
            color: 'var(--c-red)',
            rows: watchlist.decelerating || [],
        },
    ].filter((g) => g.rows.length > 0);

    /* ---- full menu: bounded top-N of products_full (see header note).
       W4 Task 15 (P6): CondensedRow rows, not BarList — see file header.
       Nothing dropped from the pre-P6 caption: units and margin_pct both
       still render, just reshaped into secondary (units) + pill (margin
       band), CondensedRow's own contract instead of one caption string. ---- */
    const menuAll = sections.products_full || [];
    const menuItems = menuAll.slice(0, MENU_TOP_N).map((p) => {
        const marginPct = Number(p.margin_pct) || 0;
        return {
            code: p.product_code,
            label: p.product_name,
            value: Number(p.revenue) || 0,
            units: Number(p.units) || 0,
            marginPct,
            tone: marginBandTone(p.margin_pct),
        };
    });
    const menuTruncated = menuAll.length > MENU_TOP_N;
    const menuVisible = menuExpanded ? menuItems : menuItems.slice(0, MENU_COLLAPSED_ROWS);
    const menuHidden = menuItems.length - menuVisible.length;

    return (
        <div className="pis-wrap">
            <style>{CSS}</style>
            {/* RangeBar sits OUTSIDE SurfaceStates so the range stays
                changeable from the error/empty states. */}
            <RangeBar
                value={preset}
                onChange={(id) => setPreset(id)}
            />
            <PullToRefresh onRefresh={load}>
                <SurfaceStates status={status} onRetry={load}>
                    {/* Hero — Product Revenue + delta vs previous period (kit
                        HeroValue: renders nothing when revNorm is null). */}
                    <HeroValue
                        label={t('kpi_product_revenue', {}, 'Product Revenue')}
                        norm={revNorm}
                        currency={currency}
                        testid="pi-hero"
                    />

                    {/* AI narrative — separate status; failure renders nothing. */}
                    <NarrativeCard
                        state={narrativeStatus}
                        text={narrative && narrative.narrative}
                        generatedAt={narrative && narrative.generated_at}
                    />

                    {/* KPI grid — quirks absorbed by the kit. */}
                    {gridItems.length > 0 && (
                        <KpiGrid currency={currency} items={gridItems} />
                    )}

                    {/* Top contributors — ranked, tappable → product detail. */}
                    <BarList
                        title={t('pulling_their_weight', {}, 'Pulling their weight')}
                        items={contributorItems}
                        formatValue={(v) => fmtMoney(v, currency)}
                        onItemPress={(item) => openDetail(item.code, item.label)}
                    />

                    {/* Falling behind — biggest decliners. */}
                    <BarList
                        title={t('falling_behind', {}, 'Falling behind')}
                        items={fallingItems}
                        formatValue={(v) => fmtMoney(v, currency)}
                        onItemPress={(item) => openDetail(item.code, item.label)}
                    />

                    {/* Category movement — tap a category to disclose its
                        subcategories in place (replaces the web's
                        expand-to-chart modal). itemProps makes each row a REAL
                        disclosure (I5): aria-expanded tracks the open panel,
                        aria-controls associates the open row with it — matching
                        Cost Management's disclosure semantics. */}
                    <BarList
                        title={t('how_categories_are_moving', {}, 'How categories are moving')}
                        items={categoryItems}
                        formatValue={(v) => fmtMoney(v, currency)}
                        onItemPress={(item) => setOpenCategory(
                            (prev) => (prev === item.label ? null : item.label),
                        )}
                        itemProps={(item) => ({
                            'aria-expanded': openCategory === item.label,
                            ...(openCategory === item.label && subItems.length > 0
                                ? { 'aria-controls': 'pi-subcategory-panel' }
                                : {}),
                        })}
                    />
                    {subItems.length > 0 && (
                        <div id="pi-subcategory-panel">
                            <BarList
                                title={openCat.category}
                                items={subItems}
                                formatValue={(v) => fmtMoney(v, currency)}
                            />
                        </div>
                    )}

                    {/* Momentum shifts — 7d run-rate vs 30d run-rate. */}
                    {velocityGroups.length > 0 && (
                        <GlassCard veil className="pis-vel" data-testid="pi-momentum">
                            <div className="insk-label">
                                {t('momentum_shifts', {}, 'Momentum shifts')}
                            </div>
                            {velocityGroups.map((g) => (
                                <div className="pis-vel-group" key={g.id}>
                                    <div className="pis-vel-title" style={{ color: g.color }}>
                                        <VelArrow up={g.up} />
                                        {g.title}
                                    </div>
                                    {g.rows.map((p) => {
                                        const pctDelta = p.delta_7d_vs_30d == null
                                            ? null
                                            : p.delta_7d_vs_30d * 100;
                                        return (
                                            <button
                                                type="button"
                                                className="pis-vel-row"
                                                key={p.product_code}
                                                data-testid="velocity-row"
                                                onClick={() => openDetail(p.product_code, p.product_name)}
                                            >
                                                <span className="pis-vel-main">
                                                    <span className="pis-vel-name" style={{ display: 'block' }}>
                                                        {p.product_name}
                                                    </span>
                                                    <span className="pis-vel-cap insk-num" style={{ display: 'block' }}>
                                                        {t(
                                                            'velocity_rates',
                                                            { s7: p.sales_last_7d, s30: p.sales_last_30d },
                                                            '{s7} in 7d · {s30} in 30d',
                                                        )}
                                                    </span>
                                                </span>
                                                {pctDelta != null && (
                                                    <span
                                                        className="pis-vel-delta insk-num"
                                                        style={{ color: g.color }}
                                                    >
                                                        {pctDelta > 0 ? '+' : ''}
                                                        {Math.round(pctDelta)}%
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            ))}
                        </GlassCard>
                    )}

                    {/* The full menu — bounded top-N, never the whole payload.
                        W4 Task 15 (P6): CondensedRow rows (see file header),
                        with the same ≤MENU_COLLAPSED_ROWS "+N more" collapse
                        BarList used to own internally. */}
                    {menuItems.length > 0 && (
                        <GlassCard veil className="insk-bars">
                            <div className="insk-label" style={{ marginBottom: 10 }}>
                                {t('the_full_menu', {}, 'The full menu')}
                            </div>
                            {menuVisible.map((item) => (
                                <CondensedRow
                                    key={item.code}
                                    icon={<IconTag />}
                                    tone={item.tone}
                                    name={item.label}
                                    value={fmtMoney(item.value, currency)}
                                    secondary={`${item.units} ${t('units', {}, 'units')}`}
                                    pill={{
                                        label: `${item.marginPct.toFixed(1)}% ${t('margin', {}, 'margin')}`,
                                        tone: item.tone,
                                    }}
                                    onPress={() => openDetail(item.code, item.label)}
                                    testId={item.code}
                                />
                            ))}
                            {menuHidden > 0 && (
                                <button
                                    type="button"
                                    className="insk-bar-more insk-num"
                                    data-testid="menu-more"
                                    onClick={() => setMenuExpanded(true)}
                                >
                                    {t('bar_list_more', { count: menuHidden }, '+{count} more')}
                                </button>
                            )}
                            {menuExpanded && menuItems.length > MENU_COLLAPSED_ROWS && (
                                <button
                                    type="button"
                                    className="insk-bar-more insk-num"
                                    data-testid="menu-less"
                                    onClick={() => setMenuExpanded(false)}
                                >
                                    {t('bar_list_less', {}, 'Show less')}
                                </button>
                            )}
                        </GlassCard>
                    )}
                    {menuTruncated && (
                        <div className="pis-menu-note insk-num" data-testid="pi-menu-note">
                            {t(
                                'showing_top_products',
                                { top: MENU_TOP_N, total: menuAll.length },
                                'Showing the top {top} of {total} products',
                            )}
                        </div>
                    )}
                </SurfaceStates>
            </PullToRefresh>
        </div>
    );
}
